import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, where, doc, updateDoc } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './InventoryPage.module.css';

function stockStatus(stock) {
  if (stock >= 20) return 'In Stock';
  if (stock >= 10) return 'Low Stock';
  return 'Critical';
}

function StatusBadge({ stock }) {
  const status = stockStatus(stock);
  const cls = { 'In Stock': styles.badgeGreen, 'Low Stock': styles.badgeYellow, 'Critical': styles.badgeRed }[status];
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`${styles.statCard} ${accent ? styles[`accent_${accent}`] : ''}`}>
      <span className={styles.statValue}>{value}</span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

export default function InventoryPage() {
  const { user }                            = useAuth();
  const [inventory, setInventory]           = useState([]);
  const [search, setSearch]                 = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editStock, setEditStock]           = useState(0);
  const [saving, setSaving]                 = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'products'), where('isArchived', '==', false));
    return onSnapshot(q, snap => {
      setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, []);

  async function handleSaveStock() {
    if (!editingProduct || saving) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'products', editingProduct.id), { stock: editStock });
      await addAuditLog(
        user.uid, user.name,
        `Adjusted stock for ${editingProduct.name}: ${editingProduct.stock} → ${editStock}`,
        'Inventory',
      );
      setEditingProduct(null);
    } catch (e) {
      console.error('Stock update failed:', e);
    } finally {
      setSaving(false);
    }
  }

  const totalProducts = inventory.length;
  const lowStockItems = inventory.filter(p => stockStatus(p.stock) === 'Low Stock').length;
  const criticalItems = inventory.filter(p => stockStatus(p.stock) === 'Critical').length;

  const categories = ['All', ...Array.from(new Set(inventory.map(p => p.category).filter(Boolean))).sort()];

  const filtered = inventory
    .filter(p => {
      const matchesSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.id.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || p.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const catCmp = (a.category ?? '').localeCompare(b.category ?? '');
      return catCmp !== 0 ? catCmp : (a.name ?? '').localeCompare(b.name ?? '');
    });

  return (
    <div className={styles.page}>
      <PageHeader
        title="Inventory Management"
        description="Product catalog and stock overview"
      />

      <div className={styles.statsRow}>
        <StatCard label="Total Products"  value={totalProducts} />
        <StatCard label="Low Stock Items" value={lowStockItems}  accent="yellow" />
        <StatCard label="Critical Items"  value={criticalItems}  accent="red" />
      </div>

      <p className={styles.note}>
        Admin can manually adjust stock levels below. Pricing is managed by the supplier.
      </p>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by product name or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.categoryFilters}>
        {categories.map(cat => (
          <button
            key={cat}
            className={`${styles.categoryPill} ${categoryFilter === cat ? styles.categoryPillActive : ''}`}
            onClick={() => setCategoryFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product ID</th>
              <th>Product Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Stock</th>
              <th>Price</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className={styles.empty}>
                  {inventory.length === 0 ? 'Loading inventory…' : 'No products match your search.'}
                </td>
              </tr>
            ) : (
              filtered.map((product, idx) => (
                <tr key={product.id} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td><img
                    src={`/${product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`}
                    alt={product.name}
                    className={styles.imgPlaceholder}
                  /></td>
                  <td className={styles.productId}>{product.id}</td>
                  <td className={styles.productName}>{product.name}</td>
                  <td>{product.category}</td>
                  <td>{product.unit ?? '—'}</td>
                  <td className={styles.stockNum}>{product.stock}</td>
                  <td className={styles.price}>₱{product.price?.toLocaleString()}</td>
                  <td><StatusBadge stock={product.stock} /></td>
                  <td>
                    <button
                      className={styles.editStockBtn}
                      onClick={() => { setEditingProduct(product); setEditStock(product.stock); }}
                    >
                      Edit Stock
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editingProduct && (
        <div className={styles.overlay} onClick={() => setEditingProduct(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Adjust Stock — {editingProduct.name}</h3>
            <p className={styles.modalBody}>Current stock: <strong>{editingProduct.stock}</strong> {editingProduct.unit ? `× ${editingProduct.unit}` : 'units'}</p>
            <label className={styles.modalLabel}>
              New Stock Quantity
              <input
                type="number"
                className={styles.modalInput}
                min={0}
                value={editStock}
                onChange={e => setEditStock(Math.max(0, Number(e.target.value)))}
              />
            </label>
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setEditingProduct(null)}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSaveStock} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
